import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRightLeft, Languages, Copy, Check, Volume2, Crown } from "lucide-react";
import { aiAPI } from "../../api";
import Chip from "../../components/atoms/Chip";
import Button from "../../components/atoms/Button";

// Speech synthesis language codes for the supported languages.
// Maps m2m100 internal codes to the BCP-47 codes the browser TTS uses.
const TTS_LANG = {
  english: "en-US", hindi: "hi-IN", tamil: "ta-IN", telugu: "te-IN",
  bengali: "bn-IN", kannada: "kn-IN", malayalam: "ml-IN", marathi: "mr-IN",
  gujarati: "gu-IN", punjabi: "pa-IN", urdu: "ur-IN",
  spanish: "es-ES", french: "fr-FR", german: "de-DE", arabic: "ar-SA",
  chinese: "zh-CN", japanese: "ja-JP", korean: "ko-KR",
  portuguese: "pt-BR", russian: "ru-RU",
};

export default function Translator() {
  const [languages, setLanguages] = useState([]);

  // Default source = user's native language (from onboarding) if set,
  // otherwise Hindi. Target defaults to English.
  const [source, setSource] = useState(() => localStorage.getItem("nativeLanguage") || "hindi");
  const [target, setTarget] = useState("english");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    aiAPI.translateLangs().then((d) => setLanguages(d.languages)).catch(() => setLanguages([]));
  }, []);

  function swap() {
    setSource(target);
    setTarget(source);
    if (result) {
      setText(result.translation);
      setResult(null);
    }
  }

  async function run() {
    if (!text.trim()) { setError("Type something first."); return; }
    setError(null);
    setLimitInfo(null);
    setLoading(true);
    setResult(null);
    try {
      const data = await aiAPI.translate(text, source, target);
      setResult(data);
    } catch (err) {
      if (err.message && /Daily limit reached/i.test(err.message)) {
        setLimitInfo({ message: err.message });
      } else {
        setError(err.message || "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  function copy() {
    if (!result?.translation) return;
    navigator.clipboard?.writeText(result.translation);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function speak(text, langCode) {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = TTS_LANG[langCode] || "en-US";
    u.rate = 0.95;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  }

  const isLoggedIn = !!localStorage.getItem("currentUser");

  return (
    <div className="page-enter" style={{ paddingBottom: 64, display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Link to="/write" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--fg-3)", fontSize: 13, fontWeight: 500, textDecoration: "none", marginBottom: 12 }}>
          <ArrowLeft size={14} strokeWidth={1.6} /> Write
        </Link>
        <Chip tone="sky" icon={<Languages size={13} strokeWidth={1.8} />}>Translator</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          One sentence, <em style={{ color: "var(--gold-500)" }}>any language.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 16, lineHeight: 1.55, margin: 0, maxWidth: "62ch" }}>
          Translate between English, Hindi, Tamil, Bengali, and 16 more. Hear the result spoken aloud, or send it to Rephrase to polish further.
        </p>
      </div>

      {/* Language picker row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <LanguageSelect value={source} onChange={setSource} languages={languages} label="From" />
        <button type="button" onClick={swap} aria-label="Swap languages"
          style={{
            background: "#fff", border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
            padding: 10, cursor: "pointer", color: "var(--fg-2)",
            transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-deep)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
          <ArrowRightLeft size={16} strokeWidth={1.6} />
        </button>
        <LanguageSelect value={target} onChange={setTarget} languages={languages} label="To" />
      </div>

      {/* Two-column input/output */}
      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Source */}
        <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p className="eyebrow">{labelFor(source, languages)}</p>
            {text && (
              <button type="button" onClick={() => speak(text, source)} aria-label="Hear original"
                style={{ background: "transparent", border: "1px solid var(--border-2)", borderRadius: 999, padding: "4px 8px", cursor: "pointer", color: "var(--fg-2)" }}>
                <Volume2 size={12} strokeWidth={1.6} />
              </button>
            )}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Type in ${labelFor(source, languages) || "your language"}…`}
            rows={7}
            style={{
              width: "100%", fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.6,
              border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
              padding: "10px 12px", resize: "vertical", background: "#fff", color: "var(--fg-1)", outline: "none",
              flex: 1,
            }}
          />
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>{text.length} / 2000</span>
        </div>

        {/* Target */}
        <div style={{ background: "#fff", border: "1px solid var(--border-1)", borderTop: result ? "3px solid var(--sky)" : "1px solid var(--border-1)", borderRadius: "var(--radius-lg)", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p className="eyebrow">{labelFor(target, languages)}</p>
            {result?.translation && (
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" onClick={() => speak(result.translation, target)} aria-label="Hear translation"
                  style={{ background: "transparent", border: "1px solid var(--border-2)", borderRadius: 999, padding: "4px 8px", cursor: "pointer", color: "var(--fg-2)" }}>
                  <Volume2 size={12} strokeWidth={1.6} />
                </button>
                <button type="button" onClick={copy} style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "4px 10px", fontSize: 11, fontWeight: 600,
                  background: copied ? "var(--sage)" : "#fff",
                  color: copied ? "var(--paper)" : "var(--fg-2)",
                  border: `1px solid ${copied ? "var(--sage)" : "var(--border-2)"}`,
                  borderRadius: 999, cursor: "pointer",
                }}>
                  {copied ? <Check size={11} strokeWidth={2} /> : <Copy size={11} strokeWidth={1.6} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            )}
          </div>
          {loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--fg-3)", fontSize: 14, minHeight: 140 }}>
              <Languages size={20} strokeWidth={1.4} style={{ opacity: 0.5, marginRight: 8 }} /> Translating…
            </div>
          ) : result ? (
            <p style={{ fontSize: 16, lineHeight: 1.6, color: "var(--fg-1)", margin: 0, whiteSpace: "pre-wrap", flex: 1, minHeight: 140 }}>
              {result.translation}
            </p>
          ) : (
            <p style={{ fontSize: 14, color: "var(--fg-3)", margin: 0, fontStyle: "italic", flex: 1, minHeight: 140, display: "flex", alignItems: "center" }}>
              The translation appears here.
            </p>
          )}
          {result?.translation && target === "english" && (
            <Link to="/write/rephrase" style={{ fontSize: 12, color: "var(--gold-700)", fontWeight: 600, textDecoration: "underline", marginTop: "auto" }}>
              Polish in Rephrase Studio →
            </Link>
          )}
        </div>
      </div>

      {/* Translate button */}
      <Button variant="primary" onClick={run} disabled={loading || !text.trim()} style={{ alignSelf: "flex-start" }}>
        {loading ? "Translating…" : "Translate"}
      </Button>

      {error && <p style={{ color: "var(--berry)", fontSize: 13, margin: 0 }}>{error}</p>}
      {limitInfo && (
        <div style={{ background: "#fdf0f1", border: "1px solid #ddb1b6", borderRadius: "var(--radius-md)", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <p style={{ color: "var(--berry-dark)", fontSize: 13, margin: 0, fontWeight: 600 }}>{limitInfo.message}</p>
          <Link to={isLoggedIn ? "/pricing" : "/signup"} style={{ textDecoration: "none" }}>
            <Button variant="primary" size="sm" iconLeft={isLoggedIn ? <Crown size={13} strokeWidth={1.8} /> : null}>
              {isLoggedIn ? "Upgrade" : "Sign up free"}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function labelFor(code, langs) {
  return langs.find((l) => l.code === code)?.label || code;
}

function LanguageSelect({ value, onChange, languages, label }) {
  return (
    <label style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.12em" }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{
          fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 600,
          padding: "10px 32px 10px 14px",
          background: "#fff", color: "var(--fg-1)",
          border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)",
          cursor: "pointer", appearance: "none",
          backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2373798a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 10px center",
          minWidth: 160,
        }}>
        <optgroup label="India">
          {languages.filter((l) => l.region === "India").map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </optgroup>
        <optgroup label="Global">
          {languages.filter((l) => l.region === "Global").map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </optgroup>
      </select>
    </label>
  );
}
